import React, {useRef, useEffect} from 'react';
import {motion} from 'motion/react';
import LeaderboardItem from './LeaderboardItem';
import {LeaderboardItemType} from '../../types';

interface LeaderboardListProps {
  data: LeaderboardItemType[];
  showHiddenItems?: boolean;
}

const LeaderboardList: React.FC<LeaderboardListProps> = ({
  data,
  showHiddenItems = false,
}) => {
  const previousDataLengthRef = useRef(0);

  useEffect(() => {
    previousDataLengthRef.current = data.length;
  }, [data.length]);

  return (
    <div
      className="mt-3 px-3 gap-1 flex flex-col transition-all"
      style={{
        paddingTop: showHiddenItems ? '2.5rem' : 0,
      }}>
      {data.map((item, i) => {
        const isTopThree = i < 3;
        const isHidden = isTopThree && !showHiddenItems;
        // Hidden items (top 3) - don't render to avoid taking up height
        if (isHidden) {
          return null;
        }

        // Revealing hidden items with special animation
        if (isTopThree && showHiddenItems) {
          return (
            <motion.div
              key={`${item.id || i}`}
              initial={{opacity: 0, scale: 1.2}}
              animate={{opacity: 1, scale: 1}}
              transition={{
                duration: 0.5,
                delay: (2 - i) * 0.1, // Reverse delay for top 3
                ease: 'easeOut',
              }}>
              <LeaderboardItem item={item} rank={item.rank!} />
            </motion.div>
          );
        }

        // Check if this is a newly added item
        const isNewItem = i >= previousDataLengthRef.current;
        const newItemIndex = i - previousDataLengthRef.current;

        // Normal items (4th and onwards)
        return (
          <motion.div
            key={`${item.id || i}`}
            initial={{opacity: 0, x: -100}}
            animate={{opacity: 1, x: 0}}
            transition={{
              duration: 0.2,
              // Only apply delay for new items, limit to max 10 new items
              delay: isNewItem ? Math.min(newItemIndex, 10) * 0.05 : 0,
              ease: 'easeOut',
            }}>
            <LeaderboardItem item={item} rank={item.rank!} />
          </motion.div>
        );
      })}
    </div>
  );
};

export default LeaderboardList;
